import { Link, useLocation } from 'react-router-dom';
import { Shield, FileText, Trash2, Sparkles, ArrowLeft } from 'lucide-react';
import { BrandLogo } from '@/components/BrandLogo';
import { LanguageToggle } from '@/components/LanguageToggle';
import { useLang } from '@/context/LanguageContext';
import { cn } from '@/lib/utils';

export const CONTACT_EMAIL = 'lifeleverageos.play.review2026@gmail.com';
export const SITE_URL = 'https://life.pewnoscsiebie.com';

const NAV = [
  { to: '/privacy', label: 'Prywatność / Privacy', icon: Shield },
  { to: '/terms', label: 'Regulamin / Terms', icon: FileText },
  { to: '/delete-account', label: 'Usuń konto / Delete', icon: Trash2 },
  { to: '/ai-notice', label: 'AI', icon: Sparkles },
];

// Small content helpers so each legal page stays clean and consistent.
export const Lang = ({ code, label, children }) => (
  <div className="space-y-5" data-testid={`legal-lang-${code.toLowerCase()}`}>
    <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-foreground/50">
      <span className="rounded bg-brand-cyan/20 px-1.5 py-0.5 text-cyan-100">{code}</span>
      {label}
    </div>
    {children}
  </div>
);

export const Sec = ({ title, children }) => (
  <section className="space-y-2">
    <h2 className="font-display text-base font-semibold text-foreground/90">{title}</h2>
    <div className="space-y-2">{children}</div>
  </section>
);

export const P = ({ children }) => (
  <p className="text-sm leading-7 text-foreground/75">{children}</p>
);

export const Bullets = ({ items }) => (
  <ul className="list-disc space-y-1.5 pl-5 text-sm leading-7 text-foreground/75 marker:text-brand-cyan/70">
    {items.map((it, i) => (
      <li key={i}>{it}</li>
    ))}
  </ul>
);

export const Divider = () => <div className="border-t border-white/10" />;

export const LegalLayout = ({ title, subtitle, updated, children }) => {
  const { pathname } = useLocation();
  const { t } = useLang();
  // Legal body text stays in its authoritative PL/EN versions; only page chrome is localized.
  const notice = t('legal.notice');
  return (
    <div className="app-canvas min-h-screen">
      <div className="mx-auto w-full max-w-3xl px-5 py-10">
        {/* Brand header */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <BrandLogo size="md" />
            <div>
              <p className="text-sm font-medium tracking-wide text-foreground/65">LIFE LEVERAGE OS</p>
              <p className="text-xs text-foreground/40">Pewnosc Siebie AI</p>
            </div>
          </div>
          <LanguageToggle />
        </div>

        {notice ? (
          <p
            data-testid="legal-language-notice"
            className="mt-6 rounded-xl border border-brand-blue/20 bg-white/[0.05] px-4 py-3 text-sm leading-6 text-foreground/75"
          >
            {notice}
          </p>
        ) : null}

        <h1 data-testid="legal-title" className="mt-8 font-display text-2xl font-semibold text-foreground">
          {title}
        </h1>
        {subtitle ? <p className="mt-1 text-sm text-foreground/55">{subtitle}</p> : null}
        {updated ? <p className="mt-1 text-xs text-foreground/40">{updated}</p> : null}

        {/* Cross-links between legal pages */}
        <nav className="mt-6 flex flex-wrap gap-2">
          {NAV.map(({ to, label, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              data-testid={`legal-link-${to.slice(1)}`}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                pathname === to
                  ? 'border-brand-cyan/40 bg-brand-cyan/15 text-cyan-100'
                  : 'border-white/10 bg-white/[0.04] text-foreground/65 hover:bg-white/[0.08]'
              )}
            >
              <Icon className="h-3.5 w-3.5" /> {label}
            </Link>
          ))}
        </nav>

        {/* Content */}
        <div className="mt-8 space-y-7">{children}</div>

        {/* Footer */}
        <footer className="mt-12 border-t border-white/10 pt-6 text-xs leading-6 text-foreground/45">
          <p>Life Leverage OS · Pewnosc Siebie AI</p>
          <p className="mt-1">
            {t('legal.website')}{' '}
            <a className="text-brand-cyan hover:underline" href={SITE_URL}>
              {SITE_URL}
            </a>
          </p>
          <p className="mt-1">
            {t('legal.contact')}{' '}
            <a className="text-brand-cyan hover:underline" href={`mailto:${CONTACT_EMAIL}`}>
              {CONTACT_EMAIL}
            </a>
          </p>
          <Link
            to="/"
            data-testid="legal-back-to-app"
            className="mt-4 inline-flex items-center gap-1.5 text-foreground/60 transition-colors hover:text-foreground/90"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> {t('legal.backToApp')}
          </Link>
        </footer>
      </div>
    </div>
  );
};
